import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { DateFilterValues } from '@components/Common/EntryFilters/DateFilterControlled';

// URL param keys
const PARAM_TERMINAL = 'terminal';
const PARAM_FROM = 'from';
const PARAM_TO = 'to';

export interface BookkeepingEntriesReportFilters {
  terminalId: string | null;
  startDate: string | null;
  endDate: string | null;
}

export interface UseBookkeepingEntriesReportFiltersResult {
  filters: BookkeepingEntriesReportFilters;
  dateFilter: DateFilterValues;
  setTerminalId: (terminalId: string | null) => void;
  setDateFilter: (value: DateFilterValues) => void;
  clearFilters: () => void;
}

/**
 * Hook to manage bookkeeping entries report filters via URL search params.
 *
 * URL format: /reports/bookkeeping-entries?terminal=xxx&from=xxx&to=xxx
 */
export function useBookkeepingEntriesReportFilters(): UseBookkeepingEntriesReportFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read values from URL
  const filters = useMemo<BookkeepingEntriesReportFilters>(() => {
    const terminalId = searchParams.get(PARAM_TERMINAL);
    const startDate = searchParams.get(PARAM_FROM);
    const endDate = searchParams.get(PARAM_TO);

    return {
      terminalId,
      startDate,
      endDate,
    };
  }, [searchParams]);

  // Date filter for the controlled component
  const dateFilter = useMemo<DateFilterValues>(() => ({
    startDate: filters.startDate,
    endDate: filters.endDate,
  }), [filters.startDate, filters.endDate]);

  // Update terminal in URL
  const setTerminalId = useCallback((terminalId: string | null) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (terminalId) {
        newParams.set(PARAM_TERMINAL, terminalId);
      } else {
        newParams.delete(PARAM_TERMINAL);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Update date filter in URL
  const setDateFilter = useCallback((value: DateFilterValues) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (value.startDate) {
        newParams.set(PARAM_FROM, value.startDate);
      } else {
        newParams.delete(PARAM_FROM);
      }

      if (value.endDate) {
        newParams.set(PARAM_TO, value.endDate);
      } else {
        newParams.delete(PARAM_TO);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  return {
    filters,
    dateFilter,
    setTerminalId,
    setDateFilter,
    clearFilters,
  };
}

export default useBookkeepingEntriesReportFilters;
